import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { translate } from '@jsverse/transloco';
import { Channel, ChannelGroup, MeasurementConfigurationModel, OrgaElementModel } from 'commons';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import type { EditGroupResponse } from './edit-group-response';

@Injectable({
  providedIn: 'root',
})
export class MetaApiService {
  private readonly http = inject(HttpClient);
  private readonly snack = inject(MatSnackBar);
  /**
   *
   */
  private readonly BASE = '/api/meta/v2/twins';
  /**
   * @returns an Observable which delievers an array of products
   */
  getTwins() {
    return this.http.get<OrgaElementModel[]>(`${this.BASE}`).pipe(
      catchError(() => {
        return of([]);
      }),
      map((orgaElements) => orgaElements?.map((oe) => new OrgaElementModel({ ...oe })) ?? []),
      tap(this.snackEmptyArr<OrgaElementModel>(translate('ERROR.NO_DATASOURCE')))
    );
  }
  /**
   * @param id
   * @returns a single product
   */
  getTwinDetail(id: number | string) {
    return this.http.get<OrgaElementModel[]>(`${this.BASE}/${id}`).pipe(map((orgaElements) => orgaElements?.map((oe) => new OrgaElementModel(oe))));
  }
  /**
   * @returns an Observable which delievers an array of groups
   * @param stackId the id of the stack
   * @returns
   */
  getTwinGroups(stackId: string) {
    return this.http
      .get<MeasurementConfigurationModel[]>(`${this.BASE}/${stackId}/groups`)
      .pipe(map((groups) => groups.map((group) => new MeasurementConfigurationModel(group))));
  }
  /**
   * @returns an Observable which delievers an array of channels
   * @param groupId
   */
  getTwinGroupsChannels(groupId: string) {
    return this.http
      .get<Channel[]>(`${this.BASE}/groups/${groupId}/channels`)
      .pipe(map((channels: Channel[]) => channels?.map((channel) => new Channel(channel)) ?? []));
  }
  /**
   * @returns an Observable which delievers an array of channels
   * @param groupId
   */
  getTwinGroup(groupId: string) {
    return this.http.get<ChannelGroup>(`${this.BASE}/groups/${groupId}`).pipe(map((channelGroup) => new ChannelGroup(channelGroup)));
  }
  /**
   *
   * @param id
   * @returns
   */
  getTwinGroupId(id: string) {
    return this.http.get<ChannelGroup>(`${this.BASE}/groups/${id}/groups`);
  }
  /**
   * @returns an Observable which delievers an array of channels
   * @param groupId
   */
  deleteTwinGroup(groupId: string) {
    return this.http.delete(`${this.BASE}/groups/${groupId}`);
  }
  /**
   *
   * @param stackId
   * @param options
   * @returns
   */
  createGroup(stackId: string, options: { name: string; description?: string }) {
    return this.http.post<Channel>(`${this.BASE}/${stackId}/groups`, options);
  }
  /**
   *
   * @param stackId
   * @param options
   * @param channels
   * @returns
   */
  createGroupAndUpdateChannels(stackId: string, options: { name: string; description?: string }, channels: string[]) {
    return this.createGroup(stackId, options).pipe(
      switchMap(({ identifier, name, description }) =>
        this.updateChannelGroup({
          identifier,
          name,
          description,
          channels,
        })
      )
    );
  }
  /**
   *
   * @param opts
   * @returns
   */
  updateChannelGroup(opts: { identifier: string; channels?: string[]; name?: string; description?: string }) {
    return this.http.put<EditGroupResponse>(`${this.BASE}/groups`, opts).pipe(
      map((res) => ({
        ...res,
        updatedConfiguration: new ChannelGroup(res.updatedConfiguration),
      }))
    );
  }
  /**
   *
   * @param msg
   * @returns
   */
  private snackEmptyArr<T>(msg: string) {
    return (arr: T[]) => {
      if (arr.length <= 0) {
        this.snack.open(msg, translate('OK'));
      }

      return arr;
    };
  }
}
